import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us", desk: true },
  { to: "/platforms", label: "Our Businesses", desk: true },
  { to: "/indril", label: "IND RIL", desk: true },
  { to: "/urgent-sell-buy", label: "Urgent Sell & Buy" },
  { to: "/boss-ai-os", label: "BOSS AI OS", desk: true },
  { to: "/founder/zahir-patel", label: "Founder", desk: true },
  { to: "/vision", label: "Vision", desk: true },
  { to: "/news", label: "News", desk: true },
  { to: "/contact", label: "Contact" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "");

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
          scrolled || open ? "bg-[#07080d]/85 backdrop-blur-xl border-[#d4af37]/15" : "bg-transparent border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="nav-logo-link" aria-label="BOSS EMPIRE home">
            <Logo />
          </Link>
          <nav className="hidden xl:flex items-center gap-1" aria-label="Primary">
            {NAV.filter((n) => n.desk).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${slug(n.label)}-link`}
                className={({ isActive }) =>
                  `px-3 py-2 text-[13px] tracking-wide transition-colors ${isActive ? "text-[#f3d77a]" : "text-slate-300 hover:text-white"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-contact-cta"
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 px-5 py-2.5 text-[13px] text-[#f3e5ab] hover:bg-[#d4af37] hover:text-black transition-colors"
            >
              Contact <ArrowUpRight className="w-4 h-4" />
            </Link>
            <button
              className="xl:hidden w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white hover:border-[#d4af37]/60 transition-colors"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-40 bg-[#07080d]/97 backdrop-blur-2xl pt-24 px-6 overflow-y-auto xl:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="mobile-menu"
          >
            <ul className="space-y-1 max-w-xl">
              {NAV.map((n, i) => (
                <motion.li
                  key={n.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={n.to}
                    data-testid={`mobile-nav-${slug(n.label)}-link`}
                    className={({ isActive }) =>
                      `flex items-baseline gap-4 py-3 border-b border-white/5 font-display text-3xl ${isActive ? "text-[#f3d77a]" : "text-white"}`
                    }
                  >
                    <span className="font-mono text-xs text-slate-500">{String(i + 1).padStart(2, "0")}</span>
                    {n.label}
                  </NavLink>
                </motion.li>
              ))}
            </ul>
            <p className="eyebrow mt-10 mb-10">Innovation • Business • Technology</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
